// SaveModuleLibraryModal.jsx
import { useState } from 'react';
import { Modal, Form, Upload } from 'antd';
import { Icon } from '../../../icon/icon';
import './SaveModuleLibraryModal.scss';
import { Divider } from 'antd';
import ButtonSecondaryLarge from '../../../common/ButtonSecondary/ButtonSecondaryLarge';
import ButtonPrimaryLarge from '../../../common/ButtonPrimary/ButtonPrimaryLarge';
import InputComponent from '../../../common/InputComponent/InputComponent';
import { openMessageError } from '../../../common/Message/openMessageError';
import { useTranslation } from 'react-i18next';

const SaveModuleLibraryModal = ({ open, onClose, onSave }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);

  const handleImageChange = info => {
    const file = info?.file?.originFileObj || info?.file;
    if (!file) return;
    setThumbnailFile(file); // giữ file gốc để upload

    const reader = new FileReader();
    reader.onload = e => setThumbnailPreview(e.target.result); // chỉ để preview
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!thumbnailFile) {
        openMessageError({ content: t('Please upload a thumbnail') });
        return;
      }
      await onSave({
        name: values.productName?.trim(),
        thumbnailFile,
      });
      form.resetFields();
      setThumbnailPreview(null);
      setThumbnailFile(null);
      onClose();
    } catch {
      /* validate fail -> antd sẽ hiển thị message */
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setThumbnailPreview(null);
    setThumbnailFile(null);
    onClose();
  };

  const uploadButton = (
    <div className="upload-box">
      <Icon.ICImage className="upload-box__icon" />
    </div>
  );

  return (
    <Modal
      title={
        <div className="flex items-center gap-8">
          <span className="text-headline-small">{t('Save Design')}</span>
        </div>
      }
      classNames={{
        header: '!p-24 !pb-16 !m-0',
        content: '!p-0',
        body: '!p-0 !m-0 flex flex-col',
      }}
      styles={{ body: { height: '500px' } }}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={720}
      height={680}
      centered
      destroyOnHidden
    >
      <div className="flex flex-col self-stretch grow">
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ flex: '130px' }}
          labelWrap
          labelAlign={'right'}
          requiredMark={(labelNode, { required }) => (
            <div className="flex items-center justify-end self-stretch">
              {labelNode}
              {required ? (
                <span style={{ color: 'red', fontSize: '12px' }}>* </span>
              ) : null}
            </div>
          )}
          colon={false}
          className="flex flex-col items-end gap-16 grow self-stretch"
          autoComplete="off"
          onFinish={handleSubmit}
        >
          <Divider type="horizontal" className="m-0 !p-0" />
          <div className="flex items-start gap-16 flex-col self-stretch grow px-24">
            <Form.Item
              label={
                <span className="required-label">{t('Product Name')}</span>
              }
              name="productName"
              className="self-stretch"
              rules={[
                { required: true, message: t('Please enter product name') },
                {
                  min: 3,
                  message: t('Product name must be at least 3 characters'),
                },
              ]}
            >
              <InputComponent placeholder={t('Enter product name')} />
            </Form.Item>

            <Form.Item
              label={<span className="required-label">{t('Thumbnail')}</span>}
              // name chỉ để canh hàng, validation cho ảnh xử lý khi submit
              required
              name="thumbnailDummy"
              className="thumbnail-uploader"
            >
              <Upload
                name="thumbnail"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleImageChange}
                accept="image/*"
              >
                {thumbnailPreview ? (
                  <img
                    src={thumbnailPreview}
                    alt="thumbnail"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
            </Form.Item>
            <Form.Item
              label={<span className="required-label">{t('Size limits')}</span>}
              layout="vertical"
              labelCol={{ flex: 'auto' }}
              labelAlign="left"
              className="self-stretch"
            >
              <div className="flex flex-wrap items-start justify-center self-stretch grow gap-16">
                <Form.Item
                  name="minHeight"
                  style={{ flex: '1 0 calc(50% - 8px)' }}
                  className="h-full"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (Number(value) <= 0)
                          return Promise.reject(
                            t('Dimension must be greater than 0')
                          );
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputComponent
                    placeholder={t('Enter minimum height')}
                    addonAfter={'mm'}
                  />
                </Form.Item>
                <Form.Item
                  name="maxHeight"
                  style={{ flex: '1 0 calc(50% - 8px)' }}
                  className="h-full"
                  dependencies={['minHeight']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const minHeight = getFieldValue('minHeight');
                        if (Number(value) <= 0)
                          return Promise.reject(
                            t('Dimension must be greater than 0')
                          );
                        if (minHeight > Number(value))
                          return Promise.reject(
                            t(
                              'Maximum dimension must be less than minimum dimension'
                            )
                          );
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputComponent
                    placeholder={t('Enter maximum height')}
                    addonAfter={'mm'}
                  />
                </Form.Item>
                <Form.Item
                  name="minWidth"
                  style={{ flex: '1 0 calc(50% - 8px)' }}
                  className="h-full"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (Number(value) <= 0)
                          return Promise.reject(
                            t('Dimension must be greater than 0')
                          );
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputComponent
                    placeholder={t('Enter minimum width')}
                    addonAfter={'mm'}
                  />
                </Form.Item>
                <Form.Item
                  name="maxWidth"
                  style={{ flex: '1 0 calc(50% - 8px)' }}
                  className="h-full"
                  dependencies={['minWidth']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const minWidth = getFieldValue('minWidth');
                        if (Number(value) <= 0)
                          return Promise.reject(
                            t('Dimension must be greater than 0')
                          );
                        if (minWidth > Number(value))
                          return Promise.reject(
                            t(
                              'Maximum dimension must be less than minimum dimension'
                            )
                          );
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputComponent
                    placeholder={t('Enter maximum width')}
                    addonAfter={'mm'}
                  />
                </Form.Item>
                <Form.Item
                  name="minDepth"
                  style={{ flex: '1 0 calc(50% - 8px)' }}
                  className="h-full"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (Number(value) <= 0)
                          return Promise.reject(
                            t('Dimension must be greater than 0')
                          );
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputComponent
                    placeholder={t('Enter minimum depth')}
                    addonAfter={'mm'}
                  />
                </Form.Item>
                <Form.Item
                  name="maxDepth"
                  style={{ flex: '1 0 calc(50% - 8px)' }}
                  className="h-full"
                  dependencies={['minDepth']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const minDepth = getFieldValue('minDepth');
                        if (Number(value) <= 0)
                          return Promise.reject(
                            t('Dimension must be greater than 0')
                          );
                        if (minDepth > Number(value))
                          return Promise.reject(
                            t(
                              'Maximum dimension must be less than minimum dimension'
                            )
                          );
                        return Promise.resolve();
                      },
                    }),
                    {
                      validator: (_, value) => {
                        if (Number(value) <= 0)
                          return Promise.reject(
                            t('Dimension must be greater than 0')
                          );
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputComponent
                    placeholder={t('Enter maximum depth')}
                    addonAfter={'mm'}
                  />
                </Form.Item>
              </div>
            </Form.Item>
          </div>
          <Divider type="horizontal" className="m-0 !p-0" />
          <div className="flex self-stretch items-center justify-end gap-16 p-24 pt-0">
            <ButtonSecondaryLarge title={t('Cancel')} type={'button'} />
            <ButtonPrimaryLarge
              title={t('Save module to library')}
              type="submit"
            />
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default SaveModuleLibraryModal;
